import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ShareFormDialog({ form, open, onOpenChange, onSaved }) {
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [roles, setRoles] = useState([]);
    const [access, setAccess] = useState({ access_user_ids: [], access_group_ids: [], access_role_ids: [] });
    const [saving, setSaving] = useState(false);

    const link = form ? `${window.location.origin}/forms/${form.id}/fill` : "";

    useEffect(() => {
        if (!open || !form) return;
        setAccess({
            access_user_ids: form.access_user_ids || [],
            access_group_ids: form.access_group_ids || [],
            access_role_ids: form.access_role_ids || [],
        });
        Promise.all([api.get("/users"), api.get("/groups"), api.get("/roles")])
            .then(([u, g, r]) => { setUsers(u.data); setGroups(g.data); setRoles(r.data); })
            .catch((e) => toast.error(extractError(e)));
    }, [open, form]);

    const toggle = (k, id) => {
        const arr = access[k];
        setAccess({ ...access, [k]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/forms/${form.id}/access`, access);
            toast.success("Access updated");
            onSaved?.();
            onOpenChange?.(false);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
        catch { toast.error("Copy failed"); }
    };

    if (!form) return null;

    const totalGranted = access.access_user_ids.length + access.access_group_ids.length + access.access_role_ids.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ShareNetwork size={20} weight="duotone" /> Share "{form.form_name}"</DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {form.status !== "published" && (
                        <div className="border border-yellow-300 bg-yellow-50 rounded-sm p-3 text-sm" data-testid="share-warn-draft">
                            <span className="font-medium">Form is in draft.</span> Recipients won't see it until you publish from the builder.
                        </div>
                    )}

                    <div>
                        <Label className="label-overline mb-2 block">Shareable link</Label>
                        <div className="flex gap-2">
                            <Input readOnly value={link} className="rounded-sm font-mono text-xs" data-testid="share-link-input" onClick={(e) => e.target.select()} />
                            <Button onClick={copyLink} variant="outline" className="rounded-sm shrink-0" data-testid="copy-link-btn"><Copy size={14} className="mr-1" /> Copy</Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Recipients must be logged in to your organization AND have access via user, group, or role below.
                            Admins always have access.
                        </p>
                    </div>

                    <div className="border-t border-border pt-6">
                        <div className="flex items-center justify-between mb-3">
                            <Label className="label-overline">Grant access</Label>
                            <Badge variant="outline" className="rounded-sm font-mono text-[10px]" data-testid="share-count">
                                {totalGranted} {totalGranted === 1 ? "grant" : "grants"}
                            </Badge>
                        </div>
                        <Tabs defaultValue="users">
                            <TabsList className="rounded-sm">
                                <TabsTrigger value="users" className="rounded-sm" data-testid="share-tab-users">Users ({access.access_user_ids.length})</TabsTrigger>
                                <TabsTrigger value="groups" className="rounded-sm" data-testid="share-tab-groups">Groups ({access.access_group_ids.length})</TabsTrigger>
                                <TabsTrigger value="roles" className="rounded-sm" data-testid="share-tab-roles">Roles ({access.access_role_ids.length})</TabsTrigger>
                            </TabsList>
                            <TabsContent value="users">
                                <ScrollList items={users} testidPrefix="share-user"
                                    selected={access.access_user_ids}
                                    onToggle={(id) => toggle("access_user_ids", id)}
                                    renderLabel={(u) => <><span className="font-medium">{u.name}</span><span className="text-muted-foreground"> · {u.email}</span></>} />
                            </TabsContent>
                            <TabsContent value="groups">
                                <ScrollList items={groups} testidPrefix="share-group"
                                    selected={access.access_group_ids}
                                    onToggle={(id) => toggle("access_group_ids", id)}
                                    renderLabel={(g) => <><span className="font-medium">{g.name}</span><span className="text-muted-foreground"> · {(g.user_ids || []).length} members</span></>} />
                            </TabsContent>
                            <TabsContent value="roles">
                                <ScrollList items={roles} testidPrefix="share-role"
                                    selected={access.access_role_ids}
                                    onToggle={(id) => toggle("access_role_ids", id)}
                                    renderLabel={(r) => <><span className="font-medium">{r.name}</span><code className="text-xs text-muted-foreground ml-2">{r.code}</code></>} />
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-share-btn">
                        {saving ? "Saving…" : "Save access"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ScrollList({ items, selected, onToggle, renderLabel, testidPrefix }) {
    if (items.length === 0) return <div className="text-xs text-muted-foreground p-4 text-center">No items</div>;
    return (
        <div className="border border-border rounded-sm max-h-60 overflow-y-auto divide-y divide-border mt-3">
            {items.map((it) => (
                <label key={it.id} className="flex items-center gap-3 p-3 text-sm hover:bg-secondary/50 cursor-pointer">
                    <Checkbox
                        checked={selected.includes(it.id)}
                        onCheckedChange={() => onToggle(it.id)}
                        data-testid={`${testidPrefix}-${it.id}`}
                    />
                    <span className="flex-1">{renderLabel(it)}</span>
                </label>
            ))}
        </div>
    );
}
